import { styled } from 'styled-components';
import { useTranslation } from 'react-i18next';
import React, { FC } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Styling } from '@/styling';
import { DialogProps } from '@/hooks/useDialog';
import { SpinningCircleLoader } from '@/components/visual/Loader';
import { Typography } from '@/components/text/Typography';
import { Box } from '@/components/layout/Box';
import { SecureButton, TertiaryButton } from '@/components/input/Button';
import { StandardCard } from '@/components/card/standard';

const DialogCard = styled(StandardCard)`
  border-top: 8px solid ${({ theme }) => theme.palette.primary.main};
  margin: 0;
  max-width: 500px;
`;

const TextSection = styled(Box).attrs(({ theme }) => ({
  direction: 'vertical',
  alignX: 'center',
  spacing: theme.spacing[2],
}))`
  margin-bottom: ${Styling.spacing(2)};
`;

const ButtonSection = styled(Box).attrs(({ theme }) => ({
  alignX: 'center',
  spacing: theme.spacing[2],
}))`
  margin-top: ${Styling.spacing(4)};
`;

const Header = styled.h2`
  ${Styling.typography('h2')};
  color: ${({ theme }) => theme.palette.primary.main};
`;

const Text = styled(Typography).attrs<{ type?: string }>({
  type: 'body',
})`
  color: #888888; // TODO move to theme
`;

const Content = styled(Box).attrs(({ theme }) => ({
  direction: 'vertical',
  alignX: 'center',
  spacing: theme.spacing[2],
}))`
  ${Styling.typography('body')};
  color: #888888; // TODO move to theme
`;

const QrContainer = styled.div`
  width: 200px;
  height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export type FrejaQrDialogDialogProps = {
  qrData?: string;
  onCancel?(): void;
} & DialogProps;

export const FrejaQrDialog: FC<FrejaQrDialogDialogProps> = ({
  qrData,
  onCancel,
  onClose,
}) => {
  const { t } = useTranslation();

  const openOnSameDevice = () => {
    window.location.href = qrData || '';
  };

  const handleCancel = () => {
    onCancel?.();
    onClose?.();
  };

  return (
    <DialogCard>
      <TextSection>
        <Header>{t('login.freja_qr_dialog.title')}</Header>
        <Text>{t('login.freja_qr_dialog.text')}</Text>
      </TextSection>
      <Content>
        <QrContainer>
          {qrData ? (
            <QRCodeCanvas value={qrData} size={180} />
          ) : (
            <SpinningCircleLoader />
          )}
        </QrContainer>
        {qrData && (
          <>
            <Typography type="body">{t('common.or')}</Typography>
            <SecureButton
              text={t('login.freja.open_this_device')}
              onClick={openOnSameDevice}
            />
          </>
        )}
      </Content>
      <ButtonSection>
        <TertiaryButton
          text={t('login.freja_qr_dialog.cancel')}
          onClick={handleCancel}
        />
      </ButtonSection>
    </DialogCard>
  );
};
